/**
 * @fileoverview Shared template-builder synchronization state pill.
 * @module template-builder/TemplateSyncStatePill
 *
 * Responsibilities:
 * - Render a compact, host-neutral save/sync status indicator for template editor headers.
 * - Normalize editor save states into the required pending/saving/synced/failed/conflict vocabulary.
 *
 * Dependencies:
 * - React for component rendering.
 *
 * Architecture:
 * - Presentation-only component; it performs no data access and never calls APIs.
 * - Host apps derive status from local-first save/sync state and pass it into this component.
 *
 * Notes:
 * - This component intentionally avoids hardcoded URLs, mock data, and storage access.
 */
import React from 'react';

/**
 * @contract TemplateSyncStatus
 *
 * Purpose:
 * - Defines the canonical editor sync-state vocabulary used by template-builder headers.
 *
 * Fields:
 * - idle: No save/sync indicator should be emphasized.
 * - pending: Local changes exist and are queued for autosave/background sync.
 * - saving: Local persistence is actively running.
 * - synced: Local changes have been persisted and are ready for background sync acknowledgement.
 * - failed: The latest local persistence attempt failed.
 * - conflict: A conflict requires user attention.
 * - offline: Local edits are allowed but remote sync is waiting for connectivity.
 *
 * Rules:
 * - Status describes local-first editor state, not direct API request state.
 *
 * Sync:
 * - Hosts may derive this value from Dexie metadata, save callbacks, or replication state.
 */
export type TemplateSyncStatus = 'idle' | 'pending' | 'saving' | 'synced' | 'failed' | 'conflict' | 'offline';

/**
 * @contract TemplateSyncStatePillProps
 *
 * Purpose:
 * - Props for rendering a template editor synchronization pill.
 *
 * Fields:
 * - status: Canonical synchronization status.
 * - pendingCount: Optional count of pending local changes.
 * - error: Optional failure/conflict message.
 * - lastSyncedAt: Optional timestamp or display string for the last successful save.
 * - compact: When true, hides secondary detail text.
 *
 * Rules:
 * - Do not pass sensitive error details; messages are visible in the UI.
 *
 * Sync:
 * - Read-only rendering of host-owned local-first state.
 */
export interface TemplateSyncStatePillProps {
  status: TemplateSyncStatus;
  pendingCount?: number;
  error?: string | null;
  lastSyncedAt?: string | null;
  compact?: boolean;
}

const STATUS_VIEW: Record<TemplateSyncStatus, { label: string; className: string; dot: string }> = {
  idle: { label: 'Ready', className: 'bg-gray-50 text-gray-500 border-gray-200', dot: 'bg-gray-300' },
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  saving: { label: 'Saving', className: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500 animate-pulse' },
  synced: { label: 'Synced', className: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  conflict: { label: 'Conflict', className: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  offline: { label: 'Offline', className: 'bg-slate-50 text-slate-700 border-slate-200', dot: 'bg-slate-500' },
};

function detailForStatus({
  status,
  pendingCount,
  error,
  lastSyncedAt,
}: TemplateSyncStatePillProps): string | null {
  if (status === 'failed' || status === 'conflict') return error ?? null;
  if (status === 'pending' && pendingCount && pendingCount > 0) return `${pendingCount} change${pendingCount === 1 ? '' : 's'}`;
  if (status === 'synced' && lastSyncedAt) return lastSyncedAt;
  return null;
}

/**
 * @component TemplateSyncStatePill
 *
 * Purpose:
 * - Shows the current local-first template editor save/sync state in a consistent header affordance.
 *
 * Props:
 * - status: Current sync state.
 * - pendingCount: Optional pending local change count.
 * - error: Optional failure/conflict message.
 * - lastSyncedAt: Optional synced detail.
 * - compact: Hide secondary detail text.
 *
 * Behavior:
 * - Renders nothing for `idle` when compact mode is enabled.
 * - Shows color-coded state, dot indicator, and optional detail text.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Never performs API calls, fetches, or storage access.
 *
 * Performance:
 * - Pure render component with no effects.
 */
export function TemplateSyncStatePill(props: TemplateSyncStatePillProps) {
  const { status, compact } = props;
  if (compact && status === 'idle') return null;
  const view = STATUS_VIEW[status];
  const detail = compact ? null : detailForStatus(props);

  return (
    <span
      role="status"
      aria-label={`Template sync status: ${view.label}${detail ? ` ${detail}` : ''}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium ${view.className}`}
      title={detail ?? view.label}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${view.dot}`} />
      <span>{view.label}</span>
      {detail && <span className="max-w-48 truncate opacity-80">{detail}</span>}
    </span>
  );
}

export default TemplateSyncStatePill;
